import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  FormHelperText,
  CircularProgress,
  Alert,
  ListSubheader,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  roleSystemKey: string;
  organizationId?: string;
}

// System role definitions for super-admin user editing
const SYSTEM_ROLES = [
  { systemKey: 'super_admin', name: 'Super Admin', category: 'administrative' },
  { systemKey: 'org_owner', name: 'Organization Owner', category: 'administrative' },
  { systemKey: 'store_owner', name: 'Store Owner', category: 'administrative' },
  { systemKey: 'store_manager', name: 'Store Manager', category: 'operational' },
  { systemKey: 'warehouse_manager', name: 'Warehouse Manager', category: 'operational' },
  { systemKey: 'store_staff', name: 'Store Staff', category: 'operational' },
  { systemKey: 'customer', name: 'Customer', category: 'combined' },
];

const CATEGORY_LABELS: Record<string, string> = {
  administrative: 'Administrative Roles',
  operational: 'Operational Roles',
  combined: 'Other Roles',
};

export const UserEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    reset,
  } = useForm<UserFormData>();

  const selectedRoleSystemKey = watch('roleSystemKey');

  const { data, isLoading, error } = useQuery({
    queryKey: ['user', id],
    queryFn: async () => {
      const response = await axiosInstance.get(`/users/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await axiosInstance.get('/organizations');
      return Array.isArray(response.data) ? response.data : response.data.data || [];
    },
  });

  // Get user's current role system key
  const getUserRoleSystemKey = (userData: any): string => {
    // Check if super admin
    if (userData.isSuperAdmin) {
      return 'super_admin';
    }
    // Check multi-role system
    if (userData.roles?.length) {
      return userData.roles[0].systemKey;
    }
    if (userData.userRoles?.length) {
      return userData.userRoles[0]?.role?.systemKey || '';
    }
    if (userData.roleSystemKeys?.length) {
      return userData.roleSystemKeys[0];
    }
    return '';
  };

  useEffect(() => {
    if (data) {
      reset({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        roleSystemKey: getUserRoleSystemKey(data),
        organizationId: data.organizationId || '',
      });
    }
  }, [data, reset]);

  const updateMutation = useMutation({
    mutationFn: async (formData: UserFormData) => {
      // Update user basic data
      const userData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        organizationId: formData.organizationId || undefined,
      };

      // Handle super admin flag
      if (formData.roleSystemKey === 'super_admin') {
        userData.isSuperAdmin = true;
      } else {
        userData.isSuperAdmin = false;
      }

      await axiosInstance.patch(`/users/${id}`, userData);

      // If organization is set, update role assignment
      if (formData.organizationId && formData.roleSystemKey !== 'super_admin') {
        try {
          const rolesResponse = await axiosInstance.get(`/permissions/roles?organizationId=${formData.organizationId}`);
          const roles = rolesResponse.data;
          const matchingRole = roles.find((r: any) => r.systemKey === formData.roleSystemKey);

          if (matchingRole) {
            await axiosInstance.post(`/permissions/users/${id}/roles`, {
              roleIds: [matchingRole.id],
            });
          }
        } catch (roleError) {
          console.warn('Could not update role:', roleError);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      showMessage('User updated successfully', 'success');
      navigate('/users');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update user', 'error');
    },
  });

  const onSubmit = (formData: UserFormData) => {
    updateMutation.mutate(formData);
  };

  // Group roles by category
  const rolesByCategory = SYSTEM_ROLES.reduce((acc, role) => {
    const category = role.category || 'combined';
    if (!acc[category]) acc[category] = [];
    acc[category].push(role);
    return acc;
  }, {} as Record<string, typeof SYSTEM_ROLES>);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          {error instanceof Error ? error.message : 'Failed to load user'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Edit User
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <TextField
              {...register('firstName', { required: 'First name is required' })}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              margin="normal"
              fullWidth
              label="First Name"
            />

            <TextField
              {...register('lastName', { required: 'Last name is required' })}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              margin="normal"
              fullWidth
              label="Last Name"
            />

            <TextField
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              error={!!errors.email}
              helperText={errors.email?.message}
              margin="normal"
              fullWidth
              label="Email"
              type="email"
            />

            <TextField
              {...register('phone', { required: 'Phone is required' })}
              error={!!errors.phone}
              helperText={errors.phone?.message}
              margin="normal"
              fullWidth
              label="Phone"
            />

            <Controller
              name="roleSystemKey"
              control={control}
              rules={{ required: 'Role is required' }}
              render={({ field, fieldState }) => (
                <FormControl fullWidth margin="normal" error={!!fieldState.error}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    {...field}
                    label="Role"
                    input={<OutlinedInput label="Role" />}
                  >
                    {(['administrative', 'operational', 'combined'] as const).flatMap((category) => {
                      const categoryRoles = rolesByCategory[category];
                      if (!categoryRoles?.length) return [];
                      return [
                        <ListSubheader key={`header-${category}`} sx={{ bgcolor: 'background.paper' }}>
                          {CATEGORY_LABELS[category]}
                        </ListSubheader>,
                        ...categoryRoles.map((role) => (
                          <MenuItem key={role.systemKey} value={role.systemKey}>
                            {role.name}
                          </MenuItem>
                        )),
                      ];
                    })}
                  </Select>
                  {fieldState.error && (
                    <FormHelperText>{fieldState.error.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            {selectedRoleSystemKey && selectedRoleSystemKey !== 'super_admin' && selectedRoleSystemKey !== 'customer' && (
              <TextField
                {...register('organizationId')}
                margin="normal"
                fullWidth
                select
                label="Organization (Optional)"
              >
                <MenuItem value="">None</MenuItem>
                {organizations?.map((org: any) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <Box display="flex" gap={2} mt={3}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/users')}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
